import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

function AdminManagement() {
  const { token, admin: currentAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [currentPasswordForCreate, setCurrentPasswordForCreate] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetPasswordUsername, setResetPasswordUsername] = useState('');
  const [resetPassword, setResetPassword] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchAdmins = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAdmins(data);
      } else {
        setError('Failed to load admin list');
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError('Failed to load admin list');
    }
  };

  useEffect(() => {
    if (token) {
      fetchAdmins();
    }
  }, [token]);

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/admin/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: newUsername,
          password: newPassword,
          currentPassword: currentPasswordForCreate // Required for security verification
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`✅ Admin "${newUsername}" created successfully!`);
        setNewUsername('');
        setNewPassword('');
        setCurrentPasswordForCreate('');
        fetchAdmins(); // Refresh the list
      } else {
        if (data.error === 'Current password is incorrect') {
          setError('🚫 Security verification failed: Your current password is incorrect');
        } else {
          setError(data.error || 'Failed to create admin');
        }
      }
    } catch (err) {
      console.error('Error creating admin:', err);
      setError('Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = async (username) => {
    if (!window.confirm(`Are you sure you want to delete admin "${username}"?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${apiUrl}/admin/${username}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Admin "${username}" deleted successfully!`);
        fetchAdmins(); // Refresh the list
      } else {
        setError(data.error || 'Failed to delete admin');
      }
    } catch (err) {
      console.error('Error deleting admin:', err);
      setError('Failed to delete admin');
    }
  };

  const handleResetPassword = async (username) => {
    // SECURITY: Require current admin password confirmation
    const currentPassword = window.prompt('🔒 SECURITY CHECK\n\nEnter YOUR current password to confirm:');

    if (!currentPassword) {
      return; // User cancelled
    }

    const newPassword = window.prompt(`Enter new password for "${username}":`);

    if (!newPassword) {
      return; // User cancelled
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${apiUrl}/admin/reset-password/${username}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          newPassword,
          currentPassword // Send current admin's password for verification
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`✅ Password reset for admin "${username}" successfully!`);
      } else {
        if (data.error === 'Current password is incorrect') {
          setError('🚫 Security verification failed: Your current password is incorrect');
        } else {
          setError(data.error || 'Failed to reset password');
        }
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError('Failed to reset password');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-3xl font-bold mb-6">Admin Management</h2>

      {/* Error and Success Messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Create New Admin Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h3 className="text-xl font-bold mb-4">🔒 Create New Admin</h3>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-yellow-800">
            <strong>Security Notice:</strong> Creating a new admin requires your current password for verification.
          </p>
        </div>
        <form onSubmit={handleCreateAdmin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">New Admin Username</label>
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter username"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">New Admin Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter password (min 8 characters)"
              minLength="8"
              required
            />
            <p className="text-sm text-gray-600 mt-1">
              Password must be at least 8 characters long
            </p>
          </div>
          <div className="border-t pt-4">
            <label className="block text-sm font-medium mb-2 text-orange-700">
              🔐 Your Current Password (for verification)
            </label>
            <input
              type="password"
              value={currentPasswordForCreate}
              onChange={(e) => setCurrentPasswordForCreate(e.target.value)}
              className="w-full px-4 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              placeholder="Enter your current password"
              required
            />
            <p className="text-sm text-gray-600 mt-1">
              For security, you must confirm your current password to create a new admin
            </p>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : '🔒 Create Admin (Secure)'}
          </button>
        </form>
      </div>

      {/* Admin List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">Current Admins</h3>
        {admins.length === 0 ? (
          <p className="text-gray-600">Loading admins...</p>
        ) : (
          <div className="space-y-4">
            {admins.map((admin) => (
              <div
                key={admin.username}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
              >
                <div>
                  <p className="font-semibold">{admin.username}</p>
                  <p className="text-sm text-gray-600">
                    Created: {new Date(admin.createdAt).toLocaleDateString()}
                    {admin.createdBy && ` by ${admin.createdBy}`}
                  </p>
                  {currentAdmin && currentAdmin.username === admin.username && (
                    <span className="inline-block mt-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                      You
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleResetPassword(admin.username)}
                    className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
                  >
                    Reset Password
                  </button>
                  {currentAdmin && currentAdmin.username !== admin.username && (
                    <button
                      onClick={() => handleDeleteAdmin(admin.username)}
                      className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminManagement;
